import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, PartyPopper, Plus, Trash2 } from "lucide-react";
import { dayjs } from "@/utils/date";
import { cn } from "@/lib/utils";
import {
  useCreateOpeningHours,
  useDeleteOpeningHours,
  useOpeningHours,
  useUpdateOpeningHours,
} from "@/hooks/use-opening-hours";
import { useClosures, useCreateClosure, useDeleteClosure } from "@/hooks/use-closures";
import { useHolidays } from "@/hooks/use-holidays";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ApiLocationClosure, ApiOpeningHours } from "@/types/api";

// 0=Mon … 6=Sun in the backend; we display in that order.
const DAYS = [
  { value: 0, label: "Lunes", short: "Lun" },
  { value: 1, label: "Martes", short: "Mar" },
  { value: 2, label: "Miércoles", short: "Mié" },
  { value: 3, label: "Jueves", short: "Jue" },
  { value: 4, label: "Viernes", short: "Vie" },
  { value: 5, label: "Sábado", short: "Sáb" },
  { value: 6, label: "Domingo", short: "Dom" },
] as const;

/** Backend gives "HH:MM:SS"; <input type="time"> needs "HH:MM". */
function toTimeInput(value: string): string {
  return value.slice(0, 5);
}

/** "18:00" → "6:00 PM". Used for read-only displays so the user sees the
 *  same format throughout the page even when the browser's native time
 *  input picker shows 24-hour. */
function formatTime12h(value: string | null): string {
  if (!value) return "—";
  const [hStr, mStr] = value.split(":");
  let h = Number(hStr);
  const m = Number(mStr);
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${period}`;
}

function formatDate(value: string): string {
  return dayjs(value)
    .format("dddd, D [de] MMMM YYYY")
    .replace(/^\w/, (c) => c.toUpperCase());
}

// ── Opening hours ───────────────────────────────────────────────

function OpeningHoursSection() {
  const { data: hours, isPending } = useOpeningHours();
  const createMut = useCreateOpeningHours();
  const updateMut = useUpdateOpeningHours();
  const deleteMut = useDeleteOpeningHours();

  // Per-day pending "new turno" form — null when the user isn't adding one.
  const [draftDay, setDraftDay] = useState<number | null>(null);
  const [draftOpen, setDraftOpen] = useState("");
  const [draftClose, setDraftClose] = useState("");
  const [draftCutoff, setDraftCutoff] = useState("");
  const [confirmDeleteBlock, setConfirmDeleteBlock] = useState<ApiOpeningHours | null>(null);

  // Group blocks by day so each day has its own card with multiple
  // open/close ranges (e.g. 12-15 + 19-23).
  const byDay = useMemo(() => {
    const map = new Map<number, ApiOpeningHours[]>();
    for (const day of DAYS) map.set(day.value, []);
    for (const h of hours ?? []) {
      const list = map.get(h.day_of_week);
      if (list) list.push(h);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.open_time.localeCompare(b.open_time));
    }
    return map;
  }, [hours]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="size-6 text-text-secondary" />
      </div>
    );
  }

  const startDraft = (dayOfWeek: number, blocks: ApiOpeningHours[]) => {
    // Default to a sensible empty 1-hour window after the latest existing
    // block, so the user can adjust without immediately overlapping.
    const last = blocks.at(-1);
    if (last) {
      const start = toTimeInput(last.close_time);
      setDraftOpen(start);
      const [h, m] = start.split(":").map(Number);
      const endH = Math.min(23, h + 1);
      setDraftClose(`${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    } else {
      setDraftOpen("12:00");
      setDraftClose("15:00");
    }
    setDraftDay(dayOfWeek);
  };

  const cancelDraft = () => {
    setDraftDay(null);
    setDraftOpen("");
    setDraftClose("");
    setDraftCutoff("");
  };

  const submitDraft = () => {
    if (draftDay === null || !draftOpen || !draftClose) return;
    if (draftOpen >= draftClose) return;
    if (draftCutoff && (draftCutoff < draftOpen || draftCutoff >= draftClose)) return;
    createMut.mutate(
      {
        day_of_week: draftDay,
        open_time: draftOpen,
        close_time: draftClose,
        last_reservation_time: draftCutoff || null,
      },
      { onSuccess: cancelDraft },
    );
  };

  const handleEditBlock = (
    block: ApiOpeningHours,
    field: "open_time" | "close_time",
    value: string,
  ) => {
    if (!value) return;
    if (toTimeInput(block[field]) === value) return;
    updateMut.mutate({ id: block.id, changes: { [field]: value } });
  };

  const handleEditCutoff = (block: ApiOpeningHours, value: string) => {
    // Empty string clears the cutoff (back to "no cutoff" / null).
    const next = value || null;
    const current = block.last_reservation_time ? toTimeInput(block.last_reservation_time) : null;
    if (next === current) return;
    updateMut.mutate({ id: block.id, changes: { last_reservation_time: next } });
  };

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-xl font-semibold tracking-tight">Horarios semanales</h2>
        <p className="text-sm text-text-secondary mt-1">
          Bloques de apertura por día. Puedes agregar varios para turnos partidos (ej. 12:00 PM –
          3:00 PM y 7:00 PM – 11:00 PM).
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
        {DAYS.map((day) => {
          const blocks = byDay.get(day.value) ?? [];
          const isClosed = blocks.length === 0 && draftDay !== day.value;
          return (
            <div
              key={day.value}
              className="flex flex-col rounded-xl border border-border bg-white overflow-hidden"
            >
              <div className="px-3 py-2 border-b border-border bg-muted/30">
                <div className="font-semibold text-sm leading-tight">{day.label}</div>
                <div className={`text-[11px] ${isClosed ? "text-red-600" : "text-text-muted"}`}>
                  {blocks.length === 0
                    ? "Cerrado"
                    : `${blocks.length} ${blocks.length === 1 ? "turno" : "turnos"}`}
                </div>
              </div>

              <div className="flex flex-col gap-2 p-2 flex-1">
                {blocks.map((block) => (
                  <div
                    key={block.id}
                    className="flex items-center gap-1 rounded-lg border border-border bg-muted/10 p-2"
                  >
                    <div className="flex-1 min-w-0 flex flex-col">
                      <Input
                        type="time"
                        defaultValue={toTimeInput(block.open_time)}
                        onBlur={(e) => handleEditBlock(block, "open_time", e.target.value)}
                        className="!h-7 w-full !text-[12px] tabular-nums !px-1 pointer-coarse:!h-7 pointer-coarse:!text-[12px] pointer-coarse:!px-1"
                      />
                      <div className="text-center text-[9px] text-text-muted my-0.5 leading-none">
                        a
                      </div>
                      <Input
                        type="time"
                        defaultValue={toTimeInput(block.close_time)}
                        onBlur={(e) => handleEditBlock(block, "close_time", e.target.value)}
                        className="!h-7 w-full !text-[12px] tabular-nums !px-1 pointer-coarse:!h-7 pointer-coarse:!text-[12px] pointer-coarse:!px-1"
                      />
                      <div
                        className="text-center text-[9px] text-text-muted mt-1.5 mb-0.5 leading-none"
                        title="Última hora a la que se aceptan reservas (opcional)"
                      >
                        recibe hasta
                      </div>
                      <Input
                        type="time"
                        defaultValue={
                          block.last_reservation_time
                            ? toTimeInput(block.last_reservation_time)
                            : ""
                        }
                        onBlur={(e) => handleEditCutoff(block, e.target.value)}
                        className="!h-7 w-full !text-[12px] tabular-nums !px-1 pointer-coarse:!h-7 pointer-coarse:!text-[12px] pointer-coarse:!px-1"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Eliminar turno"
                      onClick={() => setConfirmDeleteBlock(block)}
                      className="shrink-0 text-red-500 hover:bg-red-50 size-7"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                ))}

                {draftDay === day.value && (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 p-2 flex flex-col gap-1">
                    <Input
                      type="time"
                      value={draftOpen}
                      onChange={(e) => setDraftOpen(e.target.value)}
                      className="!h-7 w-full !text-[12px] tabular-nums !px-1 pointer-coarse:!h-7 pointer-coarse:!text-[12px] pointer-coarse:!px-1"
                    />
                    <div className="text-center text-[9px] text-text-muted leading-none">a</div>
                    <Input
                      type="time"
                      value={draftClose}
                      onChange={(e) => setDraftClose(e.target.value)}
                      className="!h-7 w-full !text-[12px] tabular-nums !px-1 pointer-coarse:!h-7 pointer-coarse:!text-[12px] pointer-coarse:!px-1"
                    />
                    <div className="text-center text-[9px] text-text-muted mt-1 leading-none">
                      recibe hasta
                    </div>
                    <Input
                      type="time"
                      value={draftCutoff}
                      onChange={(e) => setDraftCutoff(e.target.value)}
                      placeholder="opcional"
                      className="!h-7 w-full !text-[12px] tabular-nums !px-1 pointer-coarse:!h-7 pointer-coarse:!text-[12px] pointer-coarse:!px-1"
                    />
                    <div className="flex gap-1 mt-1">
                      <Button
                        size="sm"
                        onClick={submitDraft}
                        disabled={
                          !draftOpen ||
                          !draftClose ||
                          draftOpen >= draftClose ||
                          (!!draftCutoff &&
                            (draftCutoff < draftOpen || draftCutoff >= draftClose)) ||
                          createMut.isPending
                        }
                        className="flex-1 h-8 text-xs px-2"
                      >
                        OK
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelDraft}
                        className="h-8 px-2 text-xs"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                )}

                {draftDay !== day.value && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startDraft(day.value, blocks)}
                    className="h-8 w-full text-xs px-2 mt-auto"
                  >
                    <Plus className="size-3.5" /> Turno
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog
        open={confirmDeleteBlock !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteBlock(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este turno?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDeleteBlock && (
                <>
                  Vas a eliminar el turno de{" "}
                  <span className="font-medium text-foreground tabular-nums">
                    {formatTime12h(confirmDeleteBlock.open_time)} –{" "}
                    {formatTime12h(confirmDeleteBlock.close_time)}
                  </span>{" "}
                  el{" "}
                  <span className="font-medium text-foreground">
                    {DAYS.find((d) => d.value === confirmDeleteBlock.day_of_week)?.label}
                  </span>
                  . Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteBlock) {
                  deleteMut.mutate(confirmDeleteBlock.id);
                  setConfirmDeleteBlock(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// ── Closures ───────────────────────────────────────────────────

function formatRange(c: ApiLocationClosure): string {
  if (c.end_date && c.end_date !== c.date) {
    return `${formatDate(c.date)} → ${formatDate(c.end_date)}`;
  }
  return formatDate(c.date);
}

/** Format a Date as "YYYY-MM-DD" using *local* (not UTC) components. */
function toApiDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Stable module-level DayButton — defined outside the component so its
 * reference doesn't change on every render, which would force
 * react-day-picker to recreate every day cell. Absolutely-positioned to
 * fully fill its parent <td>, defeating shadcn ``Button``'s ``size-icon``
 * variant that otherwise locks it to 32×32 px (or 44×44 on touch).
 */
type DateRange = { from: Date | undefined; to: Date | undefined };

const WEEKDAYS_SHORT = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
] as const;

type DayCell = {
  date: Date;
  iso: string;
  inMonth: boolean;
};

/** Build the 6-row × 7-column day grid for a month, starting on Monday. */
function buildMonthDays(viewMonth: Date): DayCell[] {
  const first = dayjs(viewMonth).startOf("month");
  // Convert getDay (0=Sun…6=Sat) → (0=Mon…6=Sun)
  const offset = (first.day() + 6) % 7;
  const start = first.subtract(offset, "day");
  const out: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = start.add(i, "day");
    out.push({
      date: d.toDate(),
      iso: d.format("YYYY-MM-DD"),
      inMonth: d.month() === first.month(),
    });
  }
  return out;
}

function ClosureCalendar({
  pickMode,
  selectedSingle,
  selectedRange,
  closedDateSet,
  partialDateSet,
  holidaySet,
  isNoTurnoDay,
  todayStr,
  onPickSingle,
  onPickRange,
  onPickRangeComplete,
}: {
  pickMode: "single" | "range";
  selectedSingle: Date | undefined;
  selectedRange: DateRange | undefined;
  closedDateSet: Set<string>;
  partialDateSet: Set<string>;
  holidaySet: Set<string>;
  isNoTurnoDay: (d: Date) => boolean;
  todayStr: string;
  onPickSingle: (date: Date, anchor: HTMLElement) => void;
  onPickRange: (range: DateRange) => void;
  onPickRangeComplete: (range: DateRange, anchor: HTMLElement) => void;
}) {
  const [viewMonth, setViewMonth] = useState(() => dayjs().startOf("month").toDate());
  const days = useMemo(() => buildMonthDays(viewMonth), [viewMonth]);
  const monthLabel = useMemo(
    () =>
      dayjs(viewMonth)
        .format("MMMM YYYY")
        .replace(/^\w/, (c) => c.toUpperCase()),
    [viewMonth],
  );

  const selectedSingleIso = selectedSingle ? toApiDate(selectedSingle) : null;
  const rangeFromIso = selectedRange?.from ? toApiDate(selectedRange.from) : null;
  const rangeToIso = selectedRange?.to ? toApiDate(selectedRange.to) : rangeFromIso;

  const handleClick = (cell: DayCell, e: React.MouseEvent<HTMLButtonElement>) => {
    if (isNoTurnoDay(cell.date)) return;
    if (pickMode === "single") {
      onPickSingle(cell.date, e.currentTarget);
      return;
    }
    const r = selectedRange;
    if (!r?.from || (r.from && r.to)) {
      // Starting a new range — first tap, no popover yet.
      onPickRange({ from: cell.date, to: undefined });
      return;
    }
    // Completing the range on the second tap → fire the "complete" callback
    // so the parent can open the popover anchored to this end-cell button.
    const completed: DateRange =
      cell.date < r.from ? { from: cell.date, to: r.from } : { from: r.from, to: cell.date };
    onPickRange(completed);
    onPickRangeComplete(completed, e.currentTarget);
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 px-1">
        <button
          type="button"
          onClick={() => setViewMonth((d) => dayjs(d).subtract(1, "month").toDate())}
          aria-label="Mes anterior"
          className="size-7 rounded-md hover:bg-muted/40 flex items-center justify-center text-text-secondary"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-sm font-medium">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setViewMonth((d) => dayjs(d).add(1, "month").toDate())}
          aria-label="Mes siguiente"
          className="size-7 rounded-md hover:bg-muted/40 flex items-center justify-center text-text-secondary"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS_SHORT.map((w) => (
          <div
            key={w}
            className="text-[11px] uppercase tracking-wide text-text-muted text-center font-medium py-1"
          >
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((cell) => {
          const isSelectedSingle = pickMode === "single" && cell.iso === selectedSingleIso;
          const isInRange =
            pickMode === "range" &&
            rangeFromIso !== null &&
            rangeToIso !== null &&
            cell.iso >= rangeFromIso &&
            cell.iso <= rangeToIso;
          const isClosed = closedDateSet.has(cell.iso);
          const isPartial = partialDateSet.has(cell.iso);
          const isHoliday = holidaySet.has(cell.iso);
          const isNoTurno = isNoTurnoDay(cell.date);
          const isToday = cell.iso === todayStr;
          const isSelected = isSelectedSingle || isInRange;

          return (
            <button
              key={cell.iso}
              type="button"
              disabled={isNoTurno}
              onClick={(e) => handleClick(cell, e)}
              className={cn(
                "h-9 rounded-md text-sm font-medium tabular-nums flex items-center justify-center transition relative outline-none",
                !cell.inMonth && "text-text-muted/40",
                cell.inMonth && !isClosed && !isHoliday && !isPartial && "text-foreground",
                isToday &&
                  !isClosed &&
                  !isPartial &&
                  !isHoliday &&
                  "bg-emerald-100 text-emerald-900",
                isHoliday && "bg-amber-100 text-amber-800",
                isPartial && "bg-orange-100 text-orange-800",
                isClosed && "bg-red-100 text-red-800 line-through",
                isNoTurno && "!bg-slate-100 !text-slate-400 cursor-not-allowed",
                isSelected && !isNoTurno && "ring-2 ring-foreground ring-inset",
                !isNoTurno && !isSelected && "hover:bg-muted/50",
              )}
            >
              {dayjs(cell.date).date()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ClosuresSection() {
  const { data: closures, isPending } = useClosures();
  const { data: holidays } = useHolidays("CO");
  const { data: hours } = useOpeningHours();
  const createMut = useCreateClosure();
  const deleteMut = useDeleteClosure();

  // Default to single-day picking — a tap = one day. The "Rango" toggle
  // swaps the calendar into range mode (first tap = start, second = end).
  const [pickMode, setPickMode] = useState<"single" | "range">("single");
  const [singleDay, setSingleDay] = useState<Date | undefined>();
  const [range, setRange] = useState<DateRange | undefined>();
  const [reason, setReason] = useState("");
  const [partialMode, setPartialMode] = useState(false);
  const [partialStart, setPartialStart] = useState("");
  const [partialEnd, setPartialEnd] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<ApiLocationClosure | null>(null);
  // Anchor element for the floating day-popover. We capture this from the
  // click event itself so base-ui Popover can position against the actual
  // DOM node — no DOM querying or function-as-anchor (which doesn't react
  // to state changes) needed.
  const [dayAnchor, setDayAnchor] = useState<HTMLElement | null>(null);

  const todayStr = dayjs().format("YYYY-MM-DD");

  // Index closures by ISO date so the popover can show the existing
  // closure (read-only with delete) instead of a "create new" form.
  const closureByDate = useMemo(() => {
    const map = new Map<string, ApiLocationClosure>();
    for (const c of closures ?? []) {
      let d = dayjs(c.date);
      const last = dayjs(c.end_date ?? c.date);
      while (d.isBefore(last) || d.isSame(last, "day")) {
        map.set(d.format("YYYY-MM-DD"), c);
        d = d.add(1, "day");
      }
    }
    return map;
  }, [closures]);

  // ISO-string sets for fast O(1) lookup in the day grid.
  const closedDateSet = useMemo(() => {
    const out = new Set<string>();
    for (const c of closures ?? []) {
      if (c.start_time || c.end_time) continue;
      let d = dayjs(c.date);
      const last = dayjs(c.end_date ?? c.date);
      while (d.isBefore(last) || d.isSame(last, "day")) {
        out.add(d.format("YYYY-MM-DD"));
        d = d.add(1, "day");
      }
    }
    return out;
  }, [closures]);
  const partialDateSet = useMemo(() => {
    const out = new Set<string>();
    for (const c of closures ?? []) if (c.start_time && c.end_time) out.add(c.date);
    return out;
  }, [closures]);
  const holidayMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays ?? []) m.set(h.date, h.name);
    return m;
  }, [holidays]);
  const holidaySet = useMemo(() => new Set(holidayMap.keys()), [holidayMap]);

  // Weekdays without any turno → location does not operate on those days.
  // Backend day_of_week: 0=Mon … 6=Sun. JS Date.getDay(): 0=Sun … 6=Sat.
  const closedWeekdays = useMemo<Set<number>>(() => {
    const operating = new Set<number>();
    for (const h of hours ?? []) operating.add(h.day_of_week);
    const closed = new Set<number>();
    for (let i = 0; i < 7; i++) if (!operating.has(i)) closed.add(i);
    return closed;
  }, [hours]);
  const isNoTurnoDay = useMemo(
    () => (date: Date) => closedWeekdays.has((date.getDay() + 6) % 7),
    [closedWeekdays],
  );

  const fromDate = pickMode === "single" ? singleDay : range?.from;
  const toDate = pickMode === "single" ? singleDay : (range?.to ?? range?.from);
  const startApi = fromDate ? toApiDate(fromDate) : null;
  const endApi = toDate ? toApiDate(toDate) : startApi;
  const isMultiDay = startApi !== null && endApi !== null && startApi !== endApi;

  // Selected day's holiday name (single-day pick only) — auto-suggests the
  // motivo so the host doesn't have to retype "Año Nuevo" etc.
  const suggestedReason = useMemo(() => {
    if (!startApi || isMultiDay) return "";
    return holidayMap.get(startApi) ?? "";
  }, [startApi, isMultiDay, holidayMap]);

  const dayCount = useMemo(() => {
    if (!fromDate) return 0;
    return dayjs(toDate).diff(dayjs(fromDate), "day") + 1;
  }, [fromDate, toDate]);

  const canSubmit = (() => {
    if (!startApi) return false;
    if (partialMode) {
      return (
        !isMultiDay && Boolean(partialStart) && Boolean(partialEnd) && partialStart < partialEnd
      );
    }
    return true;
  })();

  const resetForm = () => {
    setSingleDay(undefined);
    setRange(undefined);
    setReason("");
    setPartialMode(false);
    setPartialStart("");
    setPartialEnd("");
    setDayAnchor(null);
  };

  const switchPickMode = (m: "single" | "range") => {
    if (m === pickMode) return;
    // Carry the current day across modes so the user doesn't lose context.
    if (m === "range" && singleDay) {
      setRange({ from: singleDay, to: undefined });
      setSingleDay(undefined);
    } else if (m === "single" && range?.from) {
      setSingleDay(range.from);
      setRange(undefined);
    }
    setPickMode(m);
  };

  const handleSubmit = () => {
    if (!canSubmit || !startApi) return;
    createMut.mutate(
      {
        date: startApi,
        end_date: isMultiDay ? endApi : null,
        start_time: partialMode ? partialStart : null,
        end_time: partialMode ? partialEnd : null,
        reason: (reason.trim() || suggestedReason).trim(),
      },
      { onSuccess: resetForm },
    );
  };

  return (
    <section className="flex flex-col gap-5">
      <header>
        <h2 className="text-xl font-semibold tracking-tight">Cierres y vacaciones</h2>
        <p className="text-sm text-text-secondary mt-1">
          Días o periodos en los que el local no recibe reservas. Útil para feriados, vacaciones,
          eventos privados o mantenimiento.
        </p>
      </header>

      <div className="rounded-xl border border-border bg-white p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 px-1">
          <span className="text-xs uppercase tracking-wide text-text-muted mr-1">Selección:</span>
          <button
            type="button"
            onClick={() => switchPickMode("single")}
            className={`h-8 px-3 rounded-lg border text-xs transition ${
              pickMode === "single"
                ? "border-foreground bg-foreground text-background font-semibold"
                : "border-input bg-background text-text-secondary hover:bg-muted/40"
            }`}
          >
            Un día
          </button>
          <button
            type="button"
            onClick={() => switchPickMode("range")}
            className={`h-8 px-3 rounded-lg border text-xs transition ${
              pickMode === "range"
                ? "border-foreground bg-foreground text-background font-semibold"
                : "border-input bg-background text-text-secondary hover:bg-muted/40"
            }`}
          >
            Rango de días
          </button>
          {pickMode === "range" && (
            <span className="text-xs text-text-muted ml-auto">
              Toca el primer día y luego el último.
            </span>
          )}
        </div>

        <ClosureCalendar
          pickMode={pickMode}
          selectedSingle={singleDay}
          selectedRange={range}
          closedDateSet={closedDateSet}
          partialDateSet={partialDateSet}
          holidaySet={holidaySet}
          isNoTurnoDay={isNoTurnoDay}
          todayStr={todayStr}
          onPickSingle={(d, anchor) => {
            setSingleDay(d);
            setDayAnchor(anchor);
          }}
          onPickRange={setRange}
          onPickRangeComplete={(_r, anchor) => setDayAnchor(anchor)}
        />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-2 pt-2 border-t border-border text-[11px] text-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-red-100 border border-red-300" />
            Cerrado
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-orange-100 border border-orange-300" />
            Parcial
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-amber-100 border border-amber-300" />
            Festivo
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-slate-100 border border-slate-300" />
            No atiende
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-emerald-100 border border-emerald-300" />
            Hoy
          </span>
        </div>
      </div>

      {/* Floating popover for single-day selection — anchored to the day cell
          via its ``data-day-iso`` attribute. */}
      <PopoverPrimitive.Root
        open={
          (pickMode === "single" && !!singleDay) ||
          (pickMode === "range" && !!range?.from && !!range?.to)
        }
        onOpenChange={(open) => {
          if (!open) resetForm();
        }}
      >
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner
            anchor={dayAnchor}
            side="bottom"
            sideOffset={8}
            className="isolate z-50"
          >
            <PopoverPrimitive.Popup className="z-50 w-80 max-w-[calc(100vw-2rem)] origin-(--transform-origin) rounded-xl bg-popover p-4 text-sm text-popover-foreground shadow-xl ring-1 ring-foreground/10 outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 flex flex-col gap-3">
              {startApi &&
                (() => {
                  // Range mode + complete range → range form (no partial,
                  // no per-day existing-closure preview).
                  if (pickMode === "range" && isMultiDay && endApi) {
                    return (
                      <>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">
                              {formatDate(startApi)} → {formatDate(endApi)}
                            </div>
                            <div className="text-xs text-text-muted">
                              {dayCount}{" "}
                              {dayCount === 1 ? "día seleccionado" : "días seleccionados"}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={resetForm}>
                            ✕
                          </Button>
                        </div>

                        <div className="flex flex-col gap-1">
                          <Label className="text-[10px] uppercase tracking-wide text-text-muted">
                            Motivo (opcional)
                          </Label>
                          <Input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Vacaciones, evento privado…"
                            className="!h-9 !text-xs !px-2 pointer-coarse:!h-9 pointer-coarse:!text-xs pointer-coarse:!px-2"
                          />
                        </div>

                        <Button
                          onClick={handleSubmit}
                          disabled={!canSubmit || createMut.isPending}
                          className="w-full"
                        >
                          <Plus className="size-4" /> Cerrar {dayCount} días
                        </Button>
                      </>
                    );
                  }

                  const existing = closureByDate.get(startApi);
                  if (existing) {
                    const isMulti = existing.end_date && existing.end_date !== existing.date;
                    const existingHolidayName = holidayMap.get(startApi);
                    return (
                      <>
                        {existingHolidayName && (
                          <div className="flex items-center gap-2 rounded-lg bg-amber-100 border border-amber-300 px-3 py-2 text-amber-900">
                            <PartyPopper className="size-4 shrink-0" />
                            <div className="flex flex-col min-w-0">
                              <span className="text-[10px] uppercase tracking-wide font-semibold leading-tight">
                                Festivo
                              </span>
                              <span className="text-sm font-medium truncate leading-tight">
                                {existingHolidayName}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">
                              {isMulti ? formatRange(existing) : formatDate(existing.date)}
                            </div>
                            <div className="text-xs text-text-muted">
                              {existing.start_time && existing.end_time
                                ? `Cerrado ${formatTime12h(existing.start_time)} – ${formatTime12h(existing.end_time)}`
                                : isMulti
                                  ? "Días completos cerrados"
                                  : "Día completo cerrado"}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={resetForm}>
                            ✕
                          </Button>
                        </div>

                        {existing.reason && (
                          <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-text-secondary italic">
                            {existing.reason}
                          </div>
                        )}

                        <Button
                          variant="destructive"
                          onClick={() => setConfirmDelete(existing)}
                          disabled={deleteMut.isPending}
                          className="w-full"
                        >
                          <Trash2 className="size-4" /> Eliminar cierre
                        </Button>
                      </>
                    );
                  }

                  const holidayName = holidayMap.get(startApi);
                  return (
                    <>
                      {holidayName && (
                        <div className="flex items-center gap-2 rounded-lg bg-amber-100 border border-amber-300 px-3 py-2 text-amber-900">
                          <PartyPopper className="size-4 shrink-0" />
                          <div className="flex flex-col min-w-0">
                            <span className="text-[10px] uppercase tracking-wide font-semibold leading-tight">
                              Festivo
                            </span>
                            <span className="text-sm font-medium truncate leading-tight">
                              {holidayName}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{formatDate(startApi)}</div>
                          <div className="text-xs text-text-muted">Cerrar este día</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={resetForm}>
                          ✕
                        </Button>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPartialMode(false)}
                          className={`flex-1 h-9 px-3 rounded-lg border text-xs transition ${
                            !partialMode
                              ? "border-foreground bg-foreground text-background font-semibold"
                              : "border-input bg-background text-text-secondary hover:bg-muted/40"
                          }`}
                        >
                          Día completo
                        </button>
                        <button
                          type="button"
                          onClick={() => setPartialMode(true)}
                          className={`flex-1 h-9 px-3 rounded-lg border text-xs transition ${
                            partialMode
                              ? "border-foreground bg-foreground text-background font-semibold"
                              : "border-input bg-background text-text-secondary hover:bg-muted/40"
                          }`}
                        >
                          Solo una franja
                        </button>
                      </div>

                      {partialMode && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex flex-col gap-1">
                            <Label className="text-[10px] uppercase tracking-wide text-text-muted">
                              Desde
                            </Label>
                            <Input
                              type="time"
                              value={partialStart}
                              onChange={(e) => setPartialStart(e.target.value)}
                              className="!h-9 !text-xs tabular-nums !px-2 pointer-coarse:!h-9 pointer-coarse:!text-xs pointer-coarse:!px-2"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <Label className="text-[10px] uppercase tracking-wide text-text-muted">
                              Hasta
                            </Label>
                            <Input
                              type="time"
                              value={partialEnd}
                              onChange={(e) => setPartialEnd(e.target.value)}
                              className="!h-9 !text-xs tabular-nums !px-2 pointer-coarse:!h-9 pointer-coarse:!text-xs pointer-coarse:!px-2"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col gap-1">
                        <Label className="text-[10px] uppercase tracking-wide text-text-muted">
                          Motivo (opcional)
                        </Label>
                        <Input
                          type="text"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder={suggestedReason || "Vacaciones, evento privado…"}
                          className="!h-9 !text-xs !px-2 pointer-coarse:!h-9 pointer-coarse:!text-xs pointer-coarse:!px-2"
                        />
                      </div>

                      <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit || createMut.isPending}
                        className="w-full"
                      >
                        <Plus className="size-4" />{" "}
                        {partialMode ? "Cerrar franja" : "Cerrar este día"}
                      </Button>
                    </>
                  );
                })()}
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {isPending && (
        <div className="flex items-center justify-center py-4">
          <Spinner className="size-5 text-text-secondary" />
        </div>
      )}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cierre?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && formatRange(confirmDelete)}. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirmDelete) deleteMut.mutate(confirmDelete.id);
                setConfirmDelete(null);
                resetForm();
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default function LocationSchedulePanel() {
  return (
    <div className="flex flex-col gap-10">
      <OpeningHoursSection />
      <ClosuresSection />
    </div>
  );
}
